import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Plus, Trash2, ChevronDown, Loader2, Search } from "lucide-react";

const getToken = () =>
  localStorage.getItem("nutramilo_access_token") ||
  sessionStorage.getItem("nutramilo_access_token");

const RELATIONS_BG = ["майка", "баща", "брат", "сестра", "син", "дъщеря", "баба", "дядо", "леля", "чичо", "първи братовчед"];
const RELATIONS_EN = ["mother", "father", "brother", "sister", "son", "daughter", "grandmother", "grandfather", "aunt", "uncle", "cousin"];
const DEGREE_OF = (rel) => {
  const first = ["мать", "майка", "баща", "брат", "сестра", "син", "дъщеря", "mother", "father", "brother", "sister", "son", "daughter"];
  const second = ["баба", "дядо", "леля", "чичо", "grandmother", "grandfather", "aunt", "uncle"];
  if (first.includes(rel.toLowerCase())) return "1";
  if (second.includes(rel.toLowerCase())) return "2";
  return "3";
};

export default function EpigeneticMapInputForm({ onSubmit, loading }) {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [diseases, setDiseases] = useState([]);
  const [diseaseLoading, setDiseaseLoading] = useState(true);
  const [diseaseSearch, setDiseaseSearch] = useState("");
  const [showDiseaseResults, setShowDiseaseResults] = useState(false);
  const [currentDiseases, setCurrentDiseases] = useState([]);
  const [familyRecords, setFamilyRecords] = useState([]);
  const [lifestyle, setLifestyle] = useState({
    smoking: "never",
    alcohol_drinks_per_week: 0,
    sleep_hours: user?.profile?.lifestyle?.sleepHours || 7,
    stress_level: "moderate",
    exercise_min_per_week: (user?.profile?.lifestyle?.activityMinutes || 0) * 7 || 150,
    bmi: user?.profile?.metrics?.bmi || 24,
    pollutants_level: "low",
  });

  // Fetch disease catalog
  const fetchDiseases = useCallback(async () => {
    try {
      const token = getToken();
      const { data } = await axios.get(`${API}/epigenetic-map/disease-catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiseases(data.diseases || []);
    } catch {
      /* silent */
    } finally {
      setDiseaseLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchDiseases();
  }, [fetchDiseases]);

  const RELATIONS = bg ? RELATIONS_BG : RELATIONS_EN;

  const addFamilyRecord = () => {
    setFamilyRecords([
      ...familyRecords,
      { disease_code: diseases[0]?.code || "T2DM", degree: "1", onset_age: null, relation: RELATIONS[0] },
    ]);
  };

  const updateFamilyRecord = (idx, field, value) => {
    const next = [...familyRecords];
    next[idx] = { ...next[idx], [field]: value };
    if (field === "relation") next[idx].degree = DEGREE_OF(value);
    setFamilyRecords(next);
  };

  const removeFamilyRecord = (idx) => {
    setFamilyRecords(familyRecords.filter((_, i) => i !== idx));
  };

  const toggleCurrent = (code) => {
    setCurrentDiseases((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // iter224 — sort diseases alphabetically by Bulgarian/English name for the
  // family history dropdown (and the live search dropdown).
  const sortedDiseases = useMemo(() => {
    return [...diseases].sort((a, b) => {
      const an = (bg ? a.name_bg : a.name_en) || a.code;
      const bn = (bg ? b.name_bg : b.name_en) || b.code;
      return an.localeCompare(bn, bg ? "bg" : "en");
    });
  }, [diseases, bg]);

  // iter223 — filter diseases for live search dropdown.
  const filteredDiseases = useMemo(() => {
    const q = diseaseSearch.trim().toLowerCase();
    if (!q) return [];
    return sortedDiseases.filter((d) =>
      d.code.toLowerCase().includes(q) ||
      (d.name_bg || "").toLowerCase().includes(q) ||
      (d.name_en || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [diseaseSearch, sortedDiseases]);

  const handleSubmit = () => {
    onSubmit({ current_diseases: currentDiseases, family_records: familyRecords, lifestyle });
  };

  const inputCls = `w-full px-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-violet-400 ${
    isDark
      ? "bg-[hsl(222,47%,8%)] border-white/10 text-white"
      : "bg-white border-gray-200 text-gray-900"
  }`;

  const cardCls = `rounded-2xl border p-4 ${
    isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"
  }`;

  if (diseaseLoading) {
    return (
      <div className={`${cardCls} flex items-center justify-center py-8`} data-testid="epimap-form-loading">
        <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="epigenetic-map-input-form">
      {/* Section 1: Current diseases — iter223 live search + chip selection */}
      <div className={cardCls}>
        <h3 className="text-sm font-bold mb-2">
          {bg ? "1. Текущи диагнози (по избор)" : "1. Current diagnoses (optional)"}
        </h3>
        <p className={`text-xs mb-3 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? "Търсете и изберете заболявания, които вече са диагностицирани." : "Search and select conditions already diagnosed."}
        </p>

        {/* Selected chips */}
        {currentDiseases.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="epimap-selected-chips">
            {currentDiseases.map((code) => {
              const d = diseases.find((x) => x.code === code) || { code, name_bg: code, name_en: code };
              return (
                <button
                  key={code}
                  onClick={() => toggleCurrent(code)}
                  data-testid={`epimap-current-${code}`}
                  className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 text-white">
                  {bg ? d.name_bg : d.name_en}
                  <span className="font-bold">×</span>
                </button>
              );
            })}
            <button onClick={() => currentDiseases.forEach(toggleCurrent)}
              className="text-[10px] underline text-gray-500" data-testid="epimap-clear-all">
              {bg ? "изчисти всички" : "clear all"}
            </button>
          </div>
        )}

        {/* Live search input */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={diseaseSearch}
            onChange={(e) => { setDiseaseSearch(e.target.value); setShowDiseaseResults(true); }}
            onFocus={() => setShowDiseaseResults(true)}
            placeholder={bg ? "Търси заболяване (живо търсене)…" : "Search disease (live search)…"}
            className={`w-full pl-9 pr-3 py-2 rounded-xl text-sm ${
              isDark ? "bg-white/5 border border-white/10 text-white placeholder:text-gray-500"
                     : "bg-white border border-gray-200 text-gray-800 placeholder:text-gray-400"}`}
            data-testid="epimap-disease-search"
          />
        </div>

        {/* Results dropdown — only when typing */}
        {showDiseaseResults && diseaseSearch.trim() && (
          <div className={`mt-2 max-h-56 overflow-y-auto rounded-xl border ${
            isDark ? "bg-[hsl(223,47%,12%)] border-white/10" : "bg-white border-gray-200"}`}
            data-testid="epimap-disease-results">
            {filteredDiseases.length === 0 ? (
              <div className="text-xs text-center py-3 text-gray-500">
                {bg ? "Няма съвпадения" : "No matches"}
              </div>
            ) : (
              filteredDiseases.map((d) => {
                const active = currentDiseases.includes(d.code);
                return (
                  <button
                    key={d.code}
                    onClick={() => { toggleCurrent(d.code); setDiseaseSearch(""); setShowDiseaseResults(false); }}
                    data-testid={`epimap-search-result-${d.code}`}
                    className={`w-full text-left text-sm px-3 py-2 flex items-center justify-between ${
                      isDark ? "hover:bg-white/5" : "hover:bg-gray-50"} ${active ? "opacity-50" : ""}`}>
                    <span>{bg ? d.name_bg : d.name_en}</span>
                    <code className="text-[10px] font-mono opacity-50">{d.code}</code>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Section 2: Family History */}
      <div className={cardCls}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold">
            {bg ? "2. Фамилна анамнеза" : "2. Family history"}
          </h3>
          <button
            onClick={addFamilyRecord}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-500/30"
            data-testid="epimap-add-family"
          >
            <Plus className="w-3 h-3" /> {bg ? "Добави" : "Add"}
          </button>
        </div>
        {familyRecords.length === 0 ? (
          <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? "Няма записи. Добавете близки роднини с диагнози." : "No records. Add relatives with diagnoses."}
          </p>
        ) : (
          <div className="space-y-2">
            {familyRecords.map((rec, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`epimap-family-row-${idx}`}>
                <select
                  value={rec.relation}
                  onChange={(e) => updateFamilyRecord(idx, "relation", e.target.value)}
                  className={`${inputCls} col-span-3 text-xs`}
                >
                  {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select
                  value={rec.disease_code}
                  onChange={(e) => updateFamilyRecord(idx, "disease_code", e.target.value)}
                  className={`${inputCls} col-span-5 text-xs`}
                >
                  {sortedDiseases.map((d) => (
                    <option key={d.code} value={d.code}>{bg ? d.name_bg : d.name_en}</option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder={bg ? "Възр. диагноза" : "Onset age"}
                  value={rec.onset_age || ""}
                  onChange={(e) => updateFamilyRecord(idx, "onset_age", e.target.value ? Number(e.target.value) : null)}
                  className={`${inputCls} col-span-3 text-xs`}
                />
                <button
                  onClick={() => removeFamilyRecord(idx)}
                  className="col-span-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 p-1.5 rounded-md"
                  data-testid={`epimap-remove-family-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 3: Lifestyle */}
      <div className={cardCls}>
        <h3 className="text-sm font-bold mb-3">
          {bg ? "3. Lifestyle (7 фактора)" : "3. Lifestyle (7 factors)"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block">{bg ? "Тютюнопушене" : "Smoking"}</label>
            <select value={lifestyle.smoking} onChange={(e) => setLifestyle({ ...lifestyle, smoking: e.target.value })} className={inputCls} data-testid="epimap-smoking">
              <option value="never">{bg ? "Никога" : "Never"}</option>
              <option value="former">{bg ? "Бивш пушач" : "Former smoker"}</option>
              <option value="current_light">{bg ? "Активен (леко)" : "Current (light)"}</option>
              <option value="current_heavy">{bg ? "Активен (тежко)" : "Current (heavy)"}</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">{bg ? "Алкохол (напитки/седм.)" : "Alcohol (drinks/wk)"}</label>
            <input type="number" min="0" value={lifestyle.alcohol_drinks_per_week} onChange={(e) => setLifestyle({ ...lifestyle, alcohol_drinks_per_week: Number(e.target.value) })} className={inputCls} data-testid="epimap-alcohol" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">{bg ? "Сън (часа/нощ)" : "Sleep (h/night)"}</label>
            <input type="number" min="0" max="14" step="0.5" value={lifestyle.sleep_hours} onChange={(e) => setLifestyle({ ...lifestyle, sleep_hours: Number(e.target.value) })} className={inputCls} data-testid="epimap-sleep" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">{bg ? "Хроничен стрес" : "Chronic stress"}</label>
            <select value={lifestyle.stress_level} onChange={(e) => setLifestyle({ ...lifestyle, stress_level: e.target.value })} className={inputCls} data-testid="epimap-stress">
              <option value="low">{bg ? "Нисък" : "Low"}</option>
              <option value="moderate">{bg ? "Умерен" : "Moderate"}</option>
              <option value="high">{bg ? "Висок" : "High"}</option>
              <option value="very_high">{bg ? "Много висок" : "Very high"}</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">{bg ? "Активност (мин./седм.)" : "Exercise (min/wk)"}</label>
            <input type="number" min="0" value={lifestyle.exercise_min_per_week} onChange={(e) => setLifestyle({ ...lifestyle, exercise_min_per_week: Number(e.target.value) })} className={inputCls} data-testid="epimap-exercise" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">BMI</label>
            <input type="number" min="10" max="60" step="0.1" value={lifestyle.bmi} onChange={(e) => setLifestyle({ ...lifestyle, bmi: Number(e.target.value) })} className={inputCls} data-testid="epimap-bmi" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium mb-1 block">{bg ? "Експозиция на замърсители" : "Pollutant exposure"}</label>
            <select value={lifestyle.pollutants_level} onChange={(e) => setLifestyle({ ...lifestyle, pollutants_level: e.target.value })} className={inputCls} data-testid="epimap-pollutants">
              <option value="low">{bg ? "Нисък" : "Low"}</option>
              <option value="moderate">{bg ? "Умерен" : "Moderate"}</option>
              <option value="high">{bg ? "Висок" : "High"}</option>
            </select>
          </div>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-violet-600 text-white font-bold shadow-md hover:shadow-lg disabled:opacity-50"
        data-testid="epimap-calculate"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {bg ? "Изчисли епигенетична карта" : "Calculate epigenetic map"}
      </button>
    </div>
  );
}
